import { NEW_UI } from "./features/orders/localPreview";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";
import type { ComponentType, ReactElement } from "react";
import { useAuthStore } from "./core/stores/useAuthStore";
import { LoginScreen } from "./features/auth/routes/LoginScreen";
import { SandboxScreen } from "./features/sandbox/routes/SandboxScreen";
import { ScaffoldWithNavBar } from "./core/components/ScaffoldWithNavBar";
import { RouteErrorScreen } from "./core/components/RouteErrorScreen";
import { InquiryFormErrorScreen } from "./features/inquiry-form/routes/InquiryFormErrorScreen";

function lazyScreen<K extends string, M extends Record<K, ComponentType>>(
  loader: () => Promise<M>,
  exportName: K,
): { lazy: () => Promise<{ Component: ComponentType }> } {
  return {
    lazy: async () => ({ Component: (await loader())[exportName] }),
  };
}

function AuthGuard(): ReactElement | null {
  const location = useLocation();
  const session = useAuthStore((s) => s.session);
  const initialized = useAuthStore((s) => s.initialized);
  if (!initialized) return NEW_UI ? <OpeningBakeryScreen /> : null;
  if (!session)
    return (
      <Navigate
        to="/login"
        replace
        state={
          NEW_UI ? { from: location.pathname + location.search + location.hash } : null
        }
      />
    );
  return <Outlet />;
}

function OpeningBakeryScreen(): ReactElement {
  return (
    <div
      role="status"
      className="grid min-h-dvh place-items-center bg-background p-8 text-sm text-muted-foreground"
    >
      Opening your bakery…
    </div>
  );
}

function PlaceholderScreen({ title }: { title: string }): ReactElement {
  return <div style={{ padding: 32 }}>{title} — coming soon</div>;
}

export const router = createBrowserRouter([
  {
    path: "/login",
    hydrateFallbackElement: NEW_UI ? <OpeningBakeryScreen /> : undefined,
    ...(NEW_UI
      ? lazyScreen(
          () => import("./features/auth/routes/LoginWorkspaceScreen"),
          "LoginWorkspaceScreen",
        )
      : { element: <LoginScreen /> }),
    errorElement: <RouteErrorScreen />,
  },
  { path: "/sandbox", element: <SandboxScreen />, errorElement: <RouteErrorScreen /> },
  {
    // Public client-facing inquiry form — no auth, no nav scaffold, own theme.
    // Branded, client-safe error screen (never the raw router default).
    path: "/i/:slug",
    errorElement: <InquiryFormErrorScreen />,
    ...lazyScreen(
      () => import("./features/inquiry-form/routes/InquiryFormScreen"),
      "InquiryFormScreen",
    ),
  },
  {
    element: <AuthGuard />,
    hydrateFallbackElement: NEW_UI ? <OpeningBakeryScreen /> : undefined,
    errorElement: <RouteErrorScreen />,
    children: [
      {
        ...(NEW_UI
          ? lazyScreen(() => import("./core/components/AppShell"), "AppShell")
          : { element: <ScaffoldWithNavBar /> }),
        children: [
          { index: true, element: <Navigate to="/orders" replace /> },
          {
            path: "/orders",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/orders/routes/OrderBoardScreen"),
                  "OrderBoardScreen",
                )
              : lazyScreen(
                  () => import("./features/orders/routes/OrderBookScreen"),
                  "OrderBookScreen",
                )),
          },
          ...(import.meta.env.DEV && NEW_UI
            ? [
                {
                  path: "/design-system",
                  ...lazyScreen(
                    () => import("./features/design-system/routes/ComponentCatalogue"),
                    "ComponentCatalogue",
                  ),
                },
              ]
            : []),
          ...(NEW_UI
            ? [
                {
                  path: "/orders/lab/new",
                  ...lazyScreen(
                    () => import("./features/orders/routes/OrderWorkspaceScreen"),
                    "OrderWorkspaceScreen",
                  ),
                },
                {
                  path: "/orders/:id/view",
                  ...lazyScreen(
                    () => import("./features/orders/routes/OrderWorkspaceScreen"),
                    "OrderWorkspaceScreen",
                  ),
                },
                {
                  path: "/orders/:id/revise",
                  ...lazyScreen(
                    () => import("./features/orders/routes/OrderWorkspaceScreen"),
                    "OrderWorkspaceScreen",
                  ),
                },
              ]
            : []),
          {
            path: "/orders/new",
            ...lazyScreen(
              () => import("./features/orders/routes/OrderComposerScreen"),
              "OrderComposerScreen",
            ),
          },
          {
            path: "/orders/:id/edit",
            ...lazyScreen(
              () => import("./features/orders/routes/OrderComposerScreen"),
              "OrderComposerScreen",
            ),
          },
          {
            path: "/inquiries",
            ...lazyScreen(
              () => import("./features/inquiries/routes/InquiriesScreen"),
              "InquiriesScreen",
            ),
          },
          {
            path: "/inquiries/:id",
            ...lazyScreen(
              () => import("./features/inquiries/routes/InquiryDetailScreen"),
              "InquiryDetailScreen",
            ),
          },
          {
            path: "/inquiries/templates/new",
            ...lazyScreen(
              () => import("./features/inquiries/routes/TemplateEditorScreen"),
              "TemplateEditorScreen",
            ),
          },
          {
            path: "/inquiries/templates/:id/edit",
            ...lazyScreen(
              () => import("./features/inquiries/routes/TemplateEditorScreen"),
              "TemplateEditorScreen",
            ),
          },
          {
            path: "/recipes",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/recipes/routes/RecipeLibraryScreen"),
                  "RecipeLibraryScreen",
                )
              : lazyScreen(
                  () => import("./features/recipes/routes/RecipeListScreen"),
                  "RecipeListScreen",
                )),
          },
          {
            path: "/recipes/new",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/recipes/routes/RecipeEditorScreen"),
                  "RecipeEditorScreen",
                )
              : lazyScreen(
                  () => import("./features/recipes/routes/RecipeFormScreen"),
                  "RecipeFormScreen",
                )),
          },
          {
            path: "/recipes/:id/edit",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/recipes/routes/RecipeEditorScreen"),
                  "RecipeEditorScreen",
                )
              : lazyScreen(
                  () => import("./features/recipes/routes/RecipeFormScreen"),
                  "RecipeFormScreen",
                )),
          },
          ...(NEW_UI
            ? [
                {
                  path: "/recipes/:id/view",
                  ...lazyScreen(
                    () => import("./features/recipes/routes/RecipeDetailScreen"),
                    "RecipeDetailScreen",
                  ),
                },
              ]
            : []),
          {
            path: "/pantry",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/pantry/routes/PantryLibraryScreen"),
                  "PantryLibraryScreen",
                )
              : lazyScreen(() => import("./features/pantry/routes/PantryScreen"), "PantryScreen")),
          },
          ...(NEW_UI
            ? [
                {
                  path: "/pantry/new",
                  ...lazyScreen(
                    () => import("./features/pantry/routes/PantryEditorScreen"),
                    "PantryEditorScreen",
                  ),
                },
                {
                  path: "/pantry/:id/edit",
                  ...lazyScreen(
                    () => import("./features/pantry/routes/PantryEditorScreen"),
                    "PantryEditorScreen",
                  ),
                },
                {
                  path: "/pantry/:id/view",
                  ...lazyScreen(
                    () => import("./features/pantry/routes/PantryDetailScreen"),
                    "PantryDetailScreen",
                  ),
                },
              ]
            : []),
          {
            path: "/packaging",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/packaging/routes/PackagingLibraryScreen"),
                  "PackagingLibraryScreen",
                )
              : lazyScreen(
                  () => import("./features/packaging/routes/PackagingScreen"),
                  "PackagingScreen",
                )),
          },
          ...(NEW_UI
            ? [
                {
                  path: "/packaging/new",
                  ...lazyScreen(
                    () => import("./features/packaging/routes/PackagingEditorScreen"),
                    "PackagingEditorScreen",
                  ),
                },
                {
                  path: "/packaging/:id/edit",
                  ...lazyScreen(
                    () => import("./features/packaging/routes/PackagingEditorScreen"),
                    "PackagingEditorScreen",
                  ),
                },
                {
                  path: "/packaging/:id/view",
                  ...lazyScreen(
                    () => import("./features/packaging/routes/PackagingDetailScreen"),
                    "PackagingDetailScreen",
                  ),
                },
              ]
            : []),
          {
            path: "/cake-pans",
            ...(NEW_UI
              ? lazyScreen(() => import("./features/pans/routes/PanLibraryScreen"), "PanLibraryScreen")
              : lazyScreen(() => import("./features/pans/routes/PansScreen"), "PansScreen")),
          },
          ...(NEW_UI ? [
            { path: "/cake-pans/new", ...lazyScreen(() => import("./features/pans/routes/PanEditorScreen"), "PanEditorScreen") },
            { path: "/cake-pans/:id/edit", ...lazyScreen(() => import("./features/pans/routes/PanEditorScreen"), "PanEditorScreen") },
            { path: "/cake-pans/:id/view", ...lazyScreen(() => import("./features/pans/routes/PanDetailScreen"), "PanDetailScreen") },
          ] : []),
          {
            path: "/baking",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/baking/routes/BakingHomeScreen"),
                  "BakingHomeScreen",
                )
              : { element: <PlaceholderScreen title="Baking" /> }),
          },
          {
            path: "/baking/order/:orderId",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/baking/routes/BakingWorkspaceScreen"),
                  "BakingWorkspaceScreen",
                )
              : lazyScreen(() => import("./features/baking"), "BakingConsoleScreen")),
          },
          {
            path: "/baking/recipes",
            ...(NEW_UI
              ? lazyScreen(
                  () => import("./features/baking/routes/BakingWorkspaceScreen"),
                  "BakingWorkspaceScreen",
                )
              : lazyScreen(() => import("./features/baking"), "BakingConsoleScreen")),
          },
          {
            path: "/settings",
            ...(NEW_UI
              ? lazyScreen(() => import("./features/settings/routes/SettingsWorkspaceScreen"), "SettingsWorkspaceScreen")
              : lazyScreen(
              () => import("./features/settings/routes/SettingsScreen"),
              "SettingsScreen",
            )),
          },
        ],
      },
    ],
  },
]);
